/**
 * Hashtag Feed Page — lists public posts + reels tagged with a hashtag.
 * Route: /hashtag/:tag
 *
 * Reuses the Strishakti-style behaviour: clicking a #tag anywhere in the app
 * navigates here. The backend endpoints are public (permitAll) but the route is
 * protected for consistency with /feed; either way the data loads.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import postService, { Post } from '@/services/postService'
import reelService, { Reel } from '@/services/reelService'
import hashtagService, { HashtagTrend } from '@/services/hashtagService'
import { HashtagText } from '@/lib/hashtagLinkify'
import GovLayout from '@/components/layout/GovLayout'
import {
  Heart, MessageCircle, Share2, Bookmark, Repeat, Trash2,
  Loader2, ArrowLeft, Hash, X, Play, Flag
} from 'lucide-react'
import ReportModal from '@/components/common/ReportModal'

const PAGE_SIZE = 15

const HashtagFeedPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>()
  const decodedTag = decodeURIComponent(tag || '').toLowerCase()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [activeTab, setActiveTab] = useState<'posts' | 'reels'>('posts')
  const [posts, setPosts] = useState<Post[]>([])
  const [reels, setReels] = useState<Reel[]>([])
  const [postsPage, setPostsPage] = useState(0)
  const [reelsPage, setReelsPage] = useState(0)
  const [postsHasMore, setPostsHasMore] = useState(true)
  const [reelsHasMore, setReelsHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [trending, setTrending] = useState<HashtagTrend[]>([])
  const [reportPostId, setReportPostId] = useState<number | null>(null)
  const [reportReelId, setReportReelId] = useState<number | null>(null)
  const lastRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Load trending sidebar once.
  useEffect(() => {
    hashtagService.getTrending(12).then(setTrending).catch(() => {})
  }, [])

  const loadPosts = useCallback(async (reset: boolean) => {
    if (reset) {
      setLoading(true)
      setPostsPage(0)
    } else {
      setLoadingMore(true)
    }
    try {
      const page = reset ? 0 : postsPage
      const res: any = await hashtagService.getPostsByHashtag(decodedTag, page, PAGE_SIZE)
      const content: Post[] = res?.content || []
      setPosts(prev => reset ? content : [...prev, ...content])
      setPostsHasMore(!res?.last && content.length === PAGE_SIZE)
      setPostsPage(page + 1)
    } catch (e) {
      console.error(e)
      setPostsHasMore(false)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [decodedTag, postsPage])

  const loadReels = useCallback(async (reset: boolean) => {
    if (reset) {
      setLoading(true)
      setReelsPage(0)
    } else {
      setLoadingMore(true)
    }
    try {
      const page = reset ? 0 : reelsPage
      const res: any = await hashtagService.getReelsByHashtag(decodedTag, page, PAGE_SIZE)
      const content: Reel[] = res?.content || []
      setReels(prev => reset ? content : [...prev, ...content])
      setReelsHasMore(!res?.last && content.length === PAGE_SIZE)
      setReelsPage(page + 1)
    } catch (e) {
      console.error(e)
      setReelsHasMore(false)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [decodedTag, reelsPage])

  // Load the active tab whenever the tag changes.
  useEffect(() => {
    setPosts([])
    setReels([])
    setPostsHasMore(true)
    setReelsHasMore(true)
    if (activeTab === 'posts') loadPosts(true)
    else loadReels(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedTag])

  // Infinite scroll.
  useEffect(() => {
    if (loading || loadingMore) return
    const hasMore = activeTab === 'posts' ? postsHasMore : reelsHasMore
    if (!hasMore) return
    const handler = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && !loadingMore) {
        if (activeTab === 'posts') loadPosts(false)
        else loadReels(false)
      }
    }
    observerRef.current = new IntersectionObserver(handler, { threshold: 0.1 })
    if (lastRef.current) observerRef.current.observe(lastRef.current)
    return () => observerRef.current?.disconnect()
  }, [loading, loadingMore, postsHasMore, reelsHasMore, activeTab, loadPosts, loadReels])

  const handleTagClick = useCallback((t: string) => {
    navigate(`/hashtag/${encodeURIComponent(t.toLowerCase())}`)
  }, [navigate])

  // ---- Post interactions (lightweight, mirrors PremiumFeedPage) ----
  const reactPost = useCallback(async (postId: number) => {
    try { await postService.react(postId, 'LIKE') } catch (e) { console.error(e) }
  }, [])

  const savePost = useCallback(async (postId: number) => {
    try { await postService.toggleSave(postId) } catch (e) { console.error(e) }
  }, [])

  const resharePost = useCallback(async (postId: number) => {
    const post = posts.find(p => p.id === postId)
    const wasReshared = post?.isReshared || false
    try {
      const res = wasReshared ? await postService.unresharePost(postId) : await postService.resharePost(postId)
      const updated = res?.data
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, isReshared: updated?.isReshared ?? !wasReshared, reshareCount: updated?.reshareCount ?? p.reshareCount }
        : p))
    } catch (e) { console.error(e) }
  }, [posts])

  const sharePost = useCallback(async (postId: number) => {
    try {
      await postService.sharePost(postId)
      const url = `${window.location.origin}/post/${postId}`
      navigator.clipboard?.writeText(url)
    } catch (e) { console.error(e) }
  }, [])

  const deletePost = useCallback(async (postId: number) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    try {
      await postService.deletePost(postId)
      setPosts(prev => prev.filter(p => p.id !== postId))
    } catch (e) {
      console.error(e)
      alert('Failed to delete post.')
    }
  }, [])

  const openComments = useCallback((postId: number) => {
    // Defer to the full feed page's comment experience if users want replies;
    // for the hashtag page we route to the post detail-ish view via the classic feed.
    navigate(`/feed/classic`)
  }, [navigate])

  // ---- Reel interactions ----
  const reshareReel = useCallback(async (reelId: number) => {
    const reel = reels.find(r => r.id === reelId)
    const wasReshared = reel?.isReshared || false
    try {
      if (wasReshared) await reelService.unreshare(reelId)
      else await reelService.reshare(reelId)
      setReels(prev => prev.map(r => r.id === reelId
        ? { ...r, isReshared: !wasReshared, reshareCount: Math.max(0, (r.reshareCount || 0) + (wasReshared ? -1 : 1)) }
        : r))
    } catch (e) { console.error(e) }
  }, [reels])

  const deleteReel = useCallback(async (reelId: number) => {
    if (!window.confirm('Delete this reel? This cannot be undone.')) return
    try {
      await reelService.delete(reelId)
      setReels(prev => prev.filter(r => r.id !== reelId))
    } catch (e) {
      console.error(e)
      alert('Failed to delete reel.')
    }
  }, [])

  if (!decodedTag) {
    return (
      <GovLayout>
        <div className="max-w-xl mx-auto py-12 text-center text-gray-500">Invalid hashtag.</div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center">
            <Hash className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">#{decodedTag}</h1>
            <p className="text-xs text-gray-500">
              {activeTab === 'posts'
                ? `${posts.length} post${posts.length === 1 ? '' : 's'}`
                : `${reels.length} reel${reels.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {(['posts', 'reels'] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setActiveTab(t)
                if (t === 'posts' && posts.length === 0) loadPosts(true)
                if (t === 'reels' && reels.length === 0) loadReels(true)
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${activeTab === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
        ) : activeTab === 'posts' ? (
          posts.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="text-4xl mb-3">🏷️</div>
              <h3 className="text-lg font-bold mb-1">No posts with #{decodedTag} yet</h3>
              <p className="text-gray-500 text-sm">Be the first to use this hashtag in a post.</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <article key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  {post.media?.[0]?.mediaUrl && (
                    post.media[0].mediaType === 'VIDEO' ? (
                      <video src={post.media[0].mediaUrl} controls className="w-full max-h-96 object-contain bg-gray-50" />
                    ) : (
                      <img src={post.media[0].mediaUrl} alt="" className="w-full max-h-96 object-contain bg-gray-50" loading="lazy" />
                    )
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img src={post.user.profileImage || '/default-avatar.svg'} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/profile/${post.user.username}`} className="font-semibold text-gray-900 text-sm truncate hover:underline">
                          {post.user.firstName} {post.user.lastName}
                        </Link>
                        <p className="text-xs text-gray-500">{new Date(post.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</p>
                      </div>
                    </div>
                    <div className="text-gray-800 text-sm mb-3 line-clamp-4">
                      <HashtagText content={post.content || ''} onTagClick={handleTagClick} />
                    </div>
                    {post.hashtags && post.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {post.hashtags.map((h) => (
                          <button key={h} onClick={() => handleTagClick(h)} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full hover:bg-blue-100">#{h}</button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1 text-gray-500 text-xs"><Heart className="w-4 h-4" /> {post.likeCount || 0}</span>
                        <button onClick={() => openComments(post.id)} className="flex items-center gap-1 text-gray-400 hover:text-blue-500 text-xs">
                          <MessageCircle className="w-4 h-4" /> {post.commentCount || 0}
                        </button>
                        <button onClick={() => resharePost(post.id)} className={`flex items-center gap-1 text-xs ${post.isReshared ? 'text-green-600' : 'text-gray-400 hover:text-green-500'}`}>
                          <Repeat className={`w-4 h-4 ${post.isReshared ? 'fill-current' : ''}`} /> {post.reshareCount || 0}
                        </button>
                        <button onClick={() => sharePost(post.id)} className="text-gray-400 hover:text-green-500"><Share2 className="w-4 h-4" /></button>
                        <button onClick={() => savePost(post.id)} className="text-gray-400 hover:text-yellow-500"><Bookmark className={`w-4 h-4 ${post.isSaved ? 'fill-yellow-500 text-yellow-500' : ''}`} /></button>
                      </div>
                      {user && post.user.id === user.id && (
                        <button onClick={() => deletePost(post.id)} className="p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      )}
                      {user && post.user.id !== user.id && (
                        <button onClick={() => setReportPostId(post.id)} className="p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500" title="Report post"><Flag className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
              {postsHasMore && (
                <div ref={lastRef} className="flex justify-center py-4">{loadingMore && <Loader2 className="w-6 h-6 animate-spin text-blue-600" />}</div>
              )}
            </>
          )
        ) : (
          reels.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="text-4xl mb-3">🎬</div>
              <h3 className="text-lg font-bold mb-1">No reels with #{decodedTag} yet</h3>
              <p className="text-gray-500 text-sm">Be the first to use this hashtag in a reel.</p>
            </div>
          ) : (
            <>
              {reels.map((reel) => (
                <article key={reel.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <Link to={`/reels`} className="block relative aspect-[9/16] max-h-[70vh] bg-black">
                    {reel.thumbnailUrl ? (
                      <img src={reel.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <video src={reel.videoUrl} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <Play className="w-12 h-12 text-white/90" />
                    </div>
                  </Link>
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <img src={reel.user.profileImage || '/default-avatar.svg'} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/profile/${reel.user.username}`} className="font-semibold text-gray-900 text-sm truncate hover:underline">
                          {reel.user.firstName} {reel.user.lastName}
                        </Link>
                        <p className="text-xs text-gray-500">{new Date(reel.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</p>
                      </div>
                    </div>
                    {reel.caption && (
                      <div className="text-gray-800 text-sm mb-2 line-clamp-3">
                        <HashtagText content={reel.caption} onTagClick={handleTagClick} tagClassName="text-blue-600 hover:underline font-medium" />
                      </div>
                    )}
                    {reel.hashtags && reel.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {reel.hashtags.map((h) => (
                          <button key={h} onClick={() => handleTagClick(h)} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full hover:bg-blue-100">#{h}</button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-gray-500 text-xs"><Heart className="w-4 h-4" /> {reel.likeCount || 0}</span>
                      <span className="flex items-center gap-1 text-gray-500 text-xs"><MessageCircle className="w-4 h-4" /> {reel.commentCount || 0}</span>
                      <button onClick={() => reshareReel(reel.id)} className={`flex items-center gap-1 text-xs ${reel.isReshared ? 'text-green-600' : 'text-gray-400 hover:text-green-500'}`}>
                        <Repeat className={`w-4 h-4 ${reel.isReshared ? 'fill-current' : ''}`} /> {reel.reshareCount || 0}
                      </button>
                      <span className="flex items-center gap-1 text-gray-500 text-xs"><Share2 className="w-4 h-4" /> {reel.shareCount || 0}</span>
                      {user && reel.user.id === user.id && (
                        <button onClick={() => deleteReel(reel.id)} className="ml-auto p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      )}
                      {user && reel.user.id !== user.id && (
                        <button onClick={() => setReportReelId(reel.id)} className="ml-auto p-1.5 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-500" title="Report reel"><Flag className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
              {reelsHasMore && (
                <div ref={lastRef} className="flex justify-center py-4">{loadingMore && <Loader2 className="w-6 h-6 animate-spin text-blue-600" />}</div>
              )}
            </>
          )
        )}

        {/* Trending sidebar */}
        {trending.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-sm mb-3 text-gray-900">Trending hashtags</h3>
            <div className="flex flex-wrap gap-2">
              {trending.map((t) => (
                <button
                  key={t.hashtag}
                  onClick={() => handleTagClick(t.hashtag)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${t.hashtag === decodedTag ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                >
                  #{t.hashtag} <span className="opacity-70">· {t.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report modals — shown when a viewer flags someone else's post/reel */}
      {reportPostId && (
        <ReportModal postId={reportPostId} onClose={() => setReportPostId(null)} />
      )}
      {reportReelId && (
        <ReportModal reelId={reportReelId} onClose={() => setReportReelId(null)} />
      )}
    </GovLayout>
  )
}

export default HashtagFeedPage